import "@/App.css";
import { useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ExecutiveOverview from "@/pages/ExecutiveOverview";
import PlanningAnalyst from "@/pages/PlanningAnalyst";
import ProductionReport from "@/pages/DailyProductionReport";
import FinancialAnalyst from "@/pages/FinancialAnalyst";
import GSTReport from "@/pages/GSTReport";
import SupplyChainAgent from "@/pages/SupplyChainAgent";
import PurchasingAnalytics from "@/pages/PurchasingAnalytics";
import Settings from "@/pages/Settings";
import UsersPage from "@/pages/UsersPage";
import AIConfigPage from "@/pages/AIConfigPage";
import OrganizationsPage from "@/pages/OrganizationsPage";
import AuditLogPage from "@/pages/AuditLogPage";
import UserManual from "@/pages/UserManual";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import OnboardingTour from "@/components/OnboardingTour";
import MesproAIFloatingWidget from "@/components/MesproAIFloatingWidget";
import { Loader2 } from "lucide-react";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const RoleOrAdminRoute = ({ role, roles, children }) => {
  const { isAdmin, hasRole } = useAuth();

  const allowed = roles || (role ? (Array.isArray(role) ? role : [role]) : []);
  if (!isAdmin && !allowed.some((r) => hasRole(r))) {
    return <Navigate to="/" replace />;
  }

  return children;
};


const LEGACY_SETTINGS_ROUTES = {
  users: "/users",
  ai: "/ai-config",
  tenants: "/organizations",
  audit: "/audit-log",
};

const SettingsRoute = () => {
  const location = useLocation();
  const legacyTab = new URLSearchParams(location.search).get("tab");
  const legacyPath = LEGACY_SETTINGS_ROUTES[legacyTab];

  return legacyPath ? <Navigate to={legacyPath} replace /> : <Settings />;
};

// Layout with Header
const AppLayout = ({ children }) => {
  const { isAdmin } = useAuth();
  const [tourForceStart, setTourForceStart] = useState(false);

  const handleRestartTour = useCallback(() => {
    setTourForceStart(true);
  }, []);

  const handleTourComplete = useCallback(() => {
    setTourForceStart(false);
  }, []);

  return (
    <AppShell onRestartTour={handleRestartTour}>
      {children}
      <OnboardingTour
        isAdmin={isAdmin}
        forceStart={tourForceStart}
        onComplete={handleTourComplete}
      />
      <MesproAIFloatingWidget />
    </AppShell>
  );
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} 
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ExecutiveOverview />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/planning"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PlanningAnalyst />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/production-report"
        element={
          <ProtectedRoute>
            <RoleOrAdminRoute roles={["daily_report", "planning_analyst"]}>
              <AppLayout>
                <ProductionReport />
              </AppLayout>
            </RoleOrAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/financial-analyst"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FinancialAnalyst />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gst-report"
        element={
          <ProtectedRoute>
            <RoleOrAdminRoute roles={["gst_report", "financial_analyst"]}>
              <AppLayout>
                <GSTReport />
              </AppLayout>
            </RoleOrAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supply-chain"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SupplyChainAgent />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchasing"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PurchasingAnalytics />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AppLayout>
                <SettingsRoute />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-manual"
        element={
          <ProtectedRoute>
            <AppLayout>
              <UserManual />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AppLayout>
                <UsersPage />
              </AppLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-config"
        element={
          <ProtectedRoute>
            <RoleOrAdminRoute role="ai_config">
              <AppLayout>
                <AIConfigPage />
              </AppLayout>
            </RoleOrAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizations"
        element={
          <ProtectedRoute>
            <RoleOrAdminRoute role="organizations">
              <AppLayout>
                <OrganizationsPage />
              </AppLayout>
            </RoleOrAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute>
            <RoleOrAdminRoute role="audit_log">
              <AppLayout>
                <AuditLogPage />
              </AppLayout>
            </RoleOrAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
